/**
 * Get every open tab onto the deployed bundle, without reloading one under the
 * person using it.
 *
 * ## Why a stale tab matters
 *
 * A stale bundle here is not a cosmetic lag. The client writes the workspace and
 * the saved screens, and an older one omits fields a newer server has since
 * begun storing — which is how `screener_preset.saved_filters` was silently
 * emptied on a live account. The old tab is not merely behind; it is writing
 * data the rest of the system will read as authoritative.
 *
 * ## The gap this closes: a tab left open never asked
 *
 * The browser checks for a new service worker when a page *loads*, and at no
 * other time. This module used to be one listener that reloaded when a new
 * worker took over, which fixed the load after a deploy — and did nothing for a
 * tab left open for days, because nothing in that tab ever looked. So it now
 * asks: every `CHECK_EVERY_MS`, and whenever the tab is shown or focused again.
 * `registration.update()` byte-compares `sw.js`, so a check that finds nothing
 * costs one small request.
 *
 * ## The new worker waits, and a bar asks (owner's call, 2026-09-21)
 *
 * This said, until 2026-09-21, that a new version was "worth a reload under the
 * user, and not a toast". That was written when an update could only be found
 * at load, where a reload costs nothing anybody sees. Once an open tab checks on
 * its own, an update lands in the middle of whatever the user is doing, and the
 * owner chose a prompt for that case.
 *
 * **The worker must wait for it** — `registerType: "prompt"`, not the
 * `autoUpdate` this used to run on. A worker that takes control of a page
 * still running the old bundle serves it the *new* precache, and the server
 * holds only the new `dist`: the old page's lazily loaded chunks — the dynamic
 * `firebase/auth` import is one — then 404, or come back as `index.html`. So
 * nothing takes over until this page says so, and when it does it reloads.
 *
 * Three rules decide when that is:
 *
 * - **Found within `SILENT_WINDOW_MS` of the page loading → applied at once,
 *   with no bar.** Somebody opening the app just after a deploy is on the old
 *   bundle for its first seconds — that is the case that once ate three
 *   consecutive deploys, each reported as "the fix you just shipped does not
 *   work" while it was live and running nowhere. Nobody is mid-gesture yet, so
 *   the reload is what they would have wanted anyway.
 * - **Found later → the bar.** Reload applies it now.
 * - **Later hides the bar, and the update is applied the next time the tab is
 *   hidden.** A reload nobody watches is free, and a tab cannot stay stale for
 *   days behind a dismissed bar. A bar that is *ignored* gets the same
 *   treatment: leaving the tab is the Later it never clicked, and the tab that
 *   stays stale because nobody answered is exactly the one this exists for.
 *
 * Reloading is cheap because the workspace is auto-saved: the persistence hook
 * flushes on `visibilitychange` to hidden and on `pagehide`, and both fire
 * before the page goes. See [persistence.md](../../../../docs/features/persistence.md).
 *
 * ## The two guards
 *
 * - **The first-ever install is not an update.** `clientsClaim` makes the first
 *   worker take control of the page that installed it, which fires
 *   `controllerchange` with no old bundle to swap — reloading there would
 *   bounce every first-time visitor once, for nothing. So a change reloads only
 *   when the page *already had* a controller, or when this page asked for it.
 * - **One reload.** `controllerchange` and the plugin's own `controlling` both
 *   announce the same takeover, and a reload that races a second one is an
 *   infinite loop that only appears in production.
 *
 * A takeover this page did *not* ask for — another tab pressed Reload — still
 * reloads it, visible or not: it is now running old code against the new
 * worker, which is the broken state the waiting exists to prevent.
 *
 * ## The deploy that switched modes
 *
 * Pages from the `autoUpdate` era never send SKIP_WAITING, so a waiting worker
 * would sit under them until every tab closed — reloads included, measured.
 * `public/sw-legacy-handoff.js` skips waiting once when the worker it replaces
 * is from that era, and retires itself; see that file.
 *
 * Everything that decides is here, with the page passed in, so the rules are
 * tested without a service worker; `components/UpdateBar.tsx` only draws.
 */

/**
 * How long after the page loads a found update is applied without asking.
 *
 * Ten seconds, measured from navigation (`performance.now()`), and it is judged
 * when the new worker is *ready*, not when it was found: the reload happens at
 * that moment, so that is the moment somebody might be mid-gesture. It covers
 * the load itself, the WASM engine booting and the first chart painting — the
 * stretch where a person is watching the app arrive rather than working in it.
 * Past it they may be typing a symbol or dragging a pane, and a reload loses
 * that. On a slow connection the new bundle can take longer than this to
 * download; that visit gets the bar instead, which is the safe way to be wrong.
 */
export const SILENT_WINDOW_MS = 10_000;

/** How often an open tab asks whether a new version has been deployed. */
export const CHECK_EVERY_MS = 10 * 60 * 1000;

/** What the bar shows: nothing, the prompt, or the prompt after Reload. */
export type BarState = "hidden" | "shown" | "applying";

export interface Updater {
  /** For `useSyncExternalStore`. */
  subscribe(listener: () => void): () => void;
  getSnapshot(): BarState;
  /** The bar's Reload: activate the waiting worker, then reload once. */
  reload(): void;
  /** The bar's Later: hide it, and apply on the next hide. */
  later(): void;
  /** Clear the timer and every listener. Tests; a page never stops. */
  stop(): void;
}

/**
 * The slice of `virtual:pwa-register`'s `registerSW` this drives.
 *
 * Stated here rather than imported: the virtual module exists only inside a
 * Vite build with the PWA plugin, so a test could not import it — `main.tsx`
 * passes the real one in.
 */
export type RegisterSW = (options: {
  onNeedRefresh?: () => void;
  onNeedReload?: () => void;
  onRegisteredSW?: (swUrl: string, registration: ServiceWorkerRegistration | undefined) => void;
  onRegisterError?: (error: unknown) => void;
}) => (reloadPage?: boolean) => Promise<void>;

export interface UpdaterOptions {
  register: RegisterSW;
  target?: Window;
  /** Milliseconds since the page loaded. */
  now?: () => number;
}

const INERT: Updater = {
  subscribe: () => () => {},
  getSnapshot: () => "hidden",
  reload: () => {},
  later: () => {},
  stop: () => {},
};

export function createUpdater({
  register,
  target = window,
  now = () => target.performance.now(),
}: UpdaterOptions): Updater {
  const container = target.navigator?.serviceWorker;
  if (!container) return INERT;
  const doc = target.document;

  const listeners = new Set<() => void>();
  let bar: BarState = "hidden";
  const setBar = (next: BarState) => {
    if (bar === next) return;
    bar = next;
    listeners.forEach((listener) => listener());
  };

  /** A new worker is installed and waiting for this page to let it in. */
  let waiting = false;
  /** Later was pressed. Only hides the bar; the hide still applies. */
  let dismissed = false;
  /** Skip-waiting has been sent; the reload follows the controller change. */
  let applying = false;
  let reloading = false;

  let registration: ServiceWorkerRegistration | undefined;
  let timer: ReturnType<typeof setInterval> | undefined;
  let checking = false;

  const reloadOnce = () => {
    if (reloading) return;
    reloading = true;
    stop();
    target.location.reload();
  };

  // Tracked rather than read once at start: with `clientsClaim` a first visit
  // goes from no controller to one, and the takeover *after* that is an update
  // that must reload. Read inside the handler it would always say "yes" — by
  // then the new worker is the controller.
  let controlled = container.controller !== null;
  const onControllerChange = () => {
    const takeover = controlled || applying;
    controlled = container.controller !== null;
    if (takeover) reloadOnce();
  };

  // Assigned before any callback can run: the plugin's callbacks all follow
  // an `await`, so the first is at least a microtask away.
  const updateSW = register({
    onNeedRefresh: () => {
      if (applying || reloading) return;
      waiting = true;
      if (now() < SILENT_WINDOW_MS) apply();
      else if (!dismissed) setBar("shown");
    },
    // The plugin reloads by itself on `controlling` unless handed this. The
    // same takeover also fires `controllerchange` below; both go through the
    // one guard, so there is one reload whichever arrives first.
    onNeedReload: reloadOnce,
    onRegisteredSW: (_swUrl, found) => {
      if (!found || reloading) return;
      registration = found;
      if (timer !== undefined) clearInterval(timer);
      timer = setInterval(check, CHECK_EVERY_MS);
    },
    onRegisterError: (error) => console.warn("[sw-update] registration failed", error),
  });

  function apply() {
    if (applying || reloading) return;
    applying = true;
    updateSW(true).catch((error: unknown) => {
      // Nothing took over, so nothing reloads: put the button back rather than
      // leave it saying "Reloading…" forever.
      applying = false;
      if (bar === "applying") setBar("shown");
      console.warn("[sw-update] could not activate the new version", error);
    });
  }

  function check() {
    if (!registration || checking || waiting || applying || reloading) return;
    checking = true;
    let asked: Promise<unknown>;
    try {
      asked = registration.update();
    } catch (error) {
      asked = Promise.reject(error);
    }
    // Swallowed: offline, it rejects on every tick, and the next tick asks
    // again. `checking` is what folds the focus and visibility events — which
    // arrive together when a tab is switched back to — into one request.
    void asked
      .catch(() => {})
      .finally(() => {
        checking = false;
      });
  }

  const onVisibility = () => {
    if (doc.visibilityState === "hidden") {
      if (waiting) apply();
    } else {
      check();
    }
  };
  const onFocus = () => check();

  container.addEventListener("controllerchange", onControllerChange);
  doc.addEventListener("visibilitychange", onVisibility);
  target.addEventListener("focus", onFocus);

  function stop() {
    if (timer !== undefined) clearInterval(timer);
    timer = undefined;
    doc.removeEventListener("visibilitychange", onVisibility);
    target.removeEventListener("focus", onFocus);
    container.removeEventListener("controllerchange", onControllerChange);
  }

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => bar,
    reload() {
      if (!waiting) return;
      if (bar === "shown") setBar("applying");
      apply();
    },
    later() {
      dismissed = true;
      setBar("hidden");
    },
    stop,
  };
}

/**
 * The page's one updater, for the bar to read.
 *
 * Module scope, for `lib/symbol-index.ts`'s reason: it is a fact about the page,
 * and the bar is drawn in three places — the desktop shell, the phone shell and
 * the sign-in screen — of which exactly one is mounted at a time.
 */
let current: Updater = INERT;

/** Start checking. Called once, from `main.tsx`, before the first render. */
export function startUpdater(options: UpdaterOptions): Updater {
  current.stop();
  current = createUpdater(options);
  return current;
}

export function currentUpdater(): Updater {
  return current;
}
